import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridDataResult, KENDO_GRID, PageChangeEvent, PagerSettings } from '@progress/kendo-angular-grid';
import { SupplierLookupService } from './supplier-lookup.service';
import { Observable } from 'rxjs';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EditSupplierLookupComponent } from './child-components/edit-supplier-lookup.component';
import { ConfirmDeleteComponent } from '../items/child-components/confirm-delete.component';
import { SupplierLookupResponseModel } from './models/supplier-lookup-response-model';

@Component({
  selector: 'app-supplier-lookup',
  standalone: true,
  imports: [CommonModule, FormsModule, KENDO_GRID, NgbModule],
  providers: [SupplierLookupService],
  templateUrl: './supplier-lookup.component.html'
})
export class SupplierLookupComponent implements OnInit {
  public view: Observable<GridDataResult>;
  public error: boolean = false;
  public errorMessage: string = '';
  public validationMessage: string = '';

  public state: State = {
    skip: 0,
    take: 20,
  };

  public pageableSettings: PagerSettings = {
    pageSizes: [10, 20, 50, 100]
  };

  public filter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [],
  };

  constructor(public supplierLookupService: SupplierLookupService, private modalService: NgbModal) {
    this.view = supplierLookupService;
    this.supplierLookupService.query(this.state, this.filter);
  }

  ngOnInit(): void {}

  public pageChange(state: PageChangeEvent): void {
    this.state.skip = state.skip;
    this.state.take = state.take;
    this.supplierLookupService.query(this.state, this.filter);
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    this.state.skip = 0;

    const stringFields = ['siteId', 'supplierId', 'siteName'];
    const invalid = stringFields.some(field => {
      const val = this.getFilterValue(filter, field);
      return val.length > 0 && val.length < 3;
    });

    if (invalid) {
      this.validationMessage = 'Please enter at least 3 characters to filter.';
      return;
    }

    this.validationMessage = '';
    this.supplierLookupService.query(this.state, this.filter);
  }

  public getFilterValue(filter: CompositeFilterDescriptor, field: string): string {
    const descriptor = this.findFieldFilter(filter, field);
    return descriptor ? String(descriptor.value ?? '') : '';
  }

  private findFieldFilter(filter: CompositeFilterDescriptor, field: string): FilterDescriptor | null {
    for (const item of filter.filters) {
      if ('filters' in item) {
        const nested = this.findFieldFilter(item, field);
        if (nested) return nested;
      } else if (item.field === field) {
        return item;
      }
    }
    return null;
  }

  public addSupplierLookup() {
    const modalRef = this.modalService.open(EditSupplierLookupComponent, { size: 'lg' });
    modalRef.componentInstance.isNew = true;

    modalRef.result.then((result) => {
      if (result?.action === 'save') {
        this.supplierLookupService.query(this.state, this.filter);
      }
    }, () => {});
  }

  public editSupplierLookup(row: SupplierLookupResponseModel) {
    const modalRef = this.modalService.open(EditSupplierLookupComponent, { size: 'lg' });
    modalRef.componentInstance.isNew = false;
    modalRef.componentInstance.supplierLookup = row;

    modalRef.result.then((result) => {
      if (result?.action === 'save') {
        this.supplierLookupService.query(this.state, this.filter);
      }
    }, () => {});
  }

  public deleteSupplierLookup(row: SupplierLookupResponseModel) {
    const modalRef = this.modalService.open(ConfirmDeleteComponent);
    modalRef.componentInstance.title = 'Confirm Delete';
    modalRef.componentInstance.message = `Are you sure you want to delete supplier lookup entry "${row.siteId}"?`;

    modalRef.result.then((result) => {
      if (result === 'confirmed') {
        this.supplierLookupService.delete(row.siteId, row.supplierId).subscribe({
          next: () => {
            this.supplierLookupService.query(this.state, this.filter);
          },
          error: (error) => {
            console.error('Error deleting supplier lookup entry:', error);
            alert('Failed to delete the supplier lookup entry. Please try again.');
          }
        });
      }
    }, () => {});
  }
}
